// AdminSettings Component - General Configuration & Tools
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  Settings, Heart, Download, Upload, Lock, Save, Loader2, RefreshCw, Percent, DollarSign
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings({ 
  contributionConfig: propContribConfig,
  adminKey, 
  onSaveContributionConfig,
  onCreateSyncPackage,
  onImportSyncPackage
}) {
  // Always call useAdmin hook
  const contextValues = useAdmin() || {};
  
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const language = contextValues.language || 'fr';

  const [contributionConfig, setContributionConfig] = useState(propContribConfig || {
    enabled: true,
    default_type: 'percent',
    default_percent: 2.0,
    default_amount: 1.0,
    custom_amount_default: false,
    title: { fr: 'Contribution volontaire à PayHub Québec+', en: 'Voluntary contribution to PayHub Quebec+' },
    description: { fr: 'Merci de soutenir votre plateforme', en: 'Thank you for supporting your platform' }
  });
  const [saving, setSaving] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');

  const handleSaveContributionConfig = async () => {
    if (onSaveContributionConfig) {
      onSaveContributionConfig(contributionConfig);
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/contribution-config', contributionConfig);
      toast.success(language === 'fr' ? 'Configuration sauvegardée!' : 'Configuration saved!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSyncPackage = async () => {
    if (onCreateSyncPackage) {
      onCreateSyncPackage();
      return;
    }
    try {
      const [notesRes, checklistRes, jobsRes] = await Promise.all([
        api.get('/admin/notes'),
        api.get('/admin/checklist'),
        api.get('/admin/job-postings')
      ]);
      
      const syncData = {
        exported_at: new Date().toISOString(),
        notes: notesRes.data.notes || [],
        checklist: checklistRes.data.items || [],
        job_postings: jobsRes.data.postings || []
      };
      
      const blob = new Blob([JSON.stringify(syncData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payhub_sync_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Package créé!' : 'Package created!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleImportSyncPackage = async (file) => {
    if (onImportSyncPackage) {
      onImportSyncPackage(file);
      return;
    }
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      let imported = { notes: 0, checklist: 0, jobs: 0 };
      
      // Import notes
      if (data.notes?.length) {
        for (const note of data.notes) {
          try {
            await api.post('/admin/notes', note);
            imported.notes++;
          } catch (e) { /* skip duplicates */ }
        }
      }
      
      // Import checklist
      if (data.checklist?.length) {
        for (const item of data.checklist) {
          try {
            await api.post('/admin/checklist', item);
            imported.checklist++;
          } catch (e) { /* skip duplicates */ }
        }
      }
      
      // Import jobs
      if (data.job_postings?.length) {
        for (const job of data.job_postings) {
          try {
            await api.post('/admin/job-postings', job);
            imported.jobs++;
          } catch (e) { /* skip duplicates */ }
        }
      }
      
      toast.success(`${language === 'fr' ? 'Importé' : 'Imported'}: ${imported.notes} notes, ${imported.checklist} tâches, ${imported.jobs} postes`);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'import' : 'Import error');
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail || !resetPassword) {
      toast.error(language === 'fr' ? 'Email et mot de passe requis' : 'Email and password required');
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/reset-password-by-email?email=${encodeURIComponent(resetEmail)}&new_password=${encodeURIComponent(resetPassword)}`,
        {},
        { headers: { 'X-Admin-Key': adminKey } }
      );
      if (response.data.success) {
        toast.success(language === 'fr' ? `Mot de passe réinitialisé pour ${resetEmail}` : `Password reset for ${resetEmail}`);
        setResetEmail('');
        setResetPassword('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gray-500 to-slate-600 flex items-center justify-center shadow-lg">
            <Settings className="h-5 w-5 text-white" />
          </div>
          {language === 'fr' ? 'Configuration Générale' : 'General Configuration'}
        </CardTitle>
        <CardDescription>
          {language === 'fr' ? 'Paramètres globaux de PayHub Québec+' : 'Global settings for PayHub Quebec+'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Site Names */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom du site (FR)' : 'Site Name (FR)'}</Label>
              <Input defaultValue="PayHub Québec" disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Nom du site (EN)' : 'Site Name (EN)'}</Label>
              <Input defaultValue="PayHub Quebec" disabled className="bg-muted" />
            </div>
          </div>

          {/* Contribution Volontaire Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5 text-blue-600" />
              {language === 'fr' ? 'Contribution Volontaire (Top-Up)' : 'Voluntary Contribution (Top-Up)'}
            </h3>
            
            <div className="space-y-4 p-4 rounded-xl border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-900/10">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">
                    {language === 'fr' ? 'Activer la contribution' : 'Enable contribution'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Affiche l\'option sur la page Top-Up' : 'Shows option on Top-Up page'}
                  </p>
                </div>
                <input 
                  type="checkbox" 
                  checked={contributionConfig.enabled}
                  onChange={(e) => setContributionConfig({...contributionConfig, enabled: e.target.checked})}
                  className="h-5 w-5 rounded"
                />
              </div>
              
              {/* Title FR/EN */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre (FR)' : 'Title (FR)'}</Label>
                  <Input 
                    value={contributionConfig.title?.fr || ''} 
                    onChange={(e) => setContributionConfig({
                      ...contributionConfig, 
                      title: {...contributionConfig.title, fr: e.target.value}
                    })}
                    placeholder="💙 Contribution volontaire à PayHub Québec+"
                    data-testid="contribution-title-fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre (EN)' : 'Title (EN)'}</Label>
                  <Input 
                    value={contributionConfig.title?.en || ''} 
                    onChange={(e) => setContributionConfig({
                      ...contributionConfig, 
                      title: {...contributionConfig.title, en: e.target.value}
                    })}
                    placeholder="💙 Voluntary contribution to PayHub Quebec+"
                    data-testid="contribution-title-en"
                  />
                </div>
              </div>
              
              {/* Description FR/EN */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Description (FR)</Label>
                  <Input 
                    value={contributionConfig.description?.fr || ''} 
                    onChange={(e) => setContributionConfig({
                      ...contributionConfig, 
                      description: {...contributionConfig.description, fr: e.target.value}
                    })}
                    placeholder="Merci de soutenir votre plateforme"
                    data-testid="contribution-desc-fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (EN)</Label>
                  <Input 
                    value={contributionConfig.description?.en || ''} 
                    onChange={(e) => setContributionConfig({
                      ...contributionConfig, 
                      description: {...contributionConfig.description, en: e.target.value}
                    })}
                    placeholder="Thank you for supporting your platform"
                    data-testid="contribution-desc-en"
                  />
                </div>
              </div>
              
              {/* Default Type & Amounts */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type par défaut' : 'Default Type'}</Label>
                  <select 
                    className="w-full p-2 border rounded-md bg-background"
                    value={contributionConfig.default_type || 'percent'}
                    onChange={(e) => setContributionConfig({...contributionConfig, default_type: e.target.value})}
                  >
                    <option value="percent">{language === 'fr' ? 'Pourcentage' : 'Percentage'}</option>
                    <option value="amount">{language === 'fr' ? 'Montant fixe' : 'Fixed Amount'}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Percent className="h-4 w-4" />
                    {language === 'fr' ? 'Pourcentage par défaut' : 'Default Percentage'}
                  </Label>
                  <Input 
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={contributionConfig.default_percent || 2}
                    onChange={(e) => setContributionConfig({...contributionConfig, default_percent: parseFloat(e.target.value) || 2})}
                    data-testid="contribution-default-percent"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    {language === 'fr' ? 'Montant par défaut' : 'Default Amount'}
                  </Label>
                  <Input 
                    type="number"
                    step="0.50"
                    min="0"
                    value={contributionConfig.default_amount || 1}
                    onChange={(e) => setContributionConfig({...contributionConfig, default_amount: parseFloat(e.target.value) || 1})}
                    data-testid="contribution-default-amount"
                  />
                </div>
              </div>
              
              {/* Custom Amount Default */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">
                    {language === 'fr' ? 'Montant personnalisé par défaut' : 'Custom Amount by Default'}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Afficher le champ personnalisé par défaut' : 'Show custom field by default'}
                  </p>
                </div>
                <input 
                  type="checkbox" 
                  checked={contributionConfig.custom_amount_default || false}
                  onChange={(e) => setContributionConfig({...contributionConfig, custom_amount_default: e.target.checked})}
                  className="h-4 w-4 rounded"
                />
              </div>
              
              {/* Save Button */}
              <Button onClick={handleSaveContributionConfig} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {language === 'fr' ? 'Sauvegarder la configuration' : 'Save Configuration'}
              </Button>
            </div>
          </div>
          
          {/* Sync & Tools Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              {language === 'fr' ? 'Sync & Outils' : 'Sync & Tools'}
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Create Sync Package */}
              <div className="p-4 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/50">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Download className="h-4 w-4 text-blue-600" />
                  {language === 'fr' ? 'Créer Package de Sync' : 'Create Sync Package'}
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' 
                    ? 'Exporte Notes, Checklist et Positions en un seul fichier' 
                    : 'Export Notes, Checklist and Positions in one file'}
                </p>
                <Button onClick={handleCreateSyncPackage} className="w-full bg-blue-600 hover:bg-blue-700">
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Télécharger Package' : 'Download Package'}
                </Button>
              </div>
              
              {/* Import Sync Package */}
              <div className="p-4 rounded-xl border-2 border-dashed border-green-300 bg-green-50/50">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-green-600" />
                  {language === 'fr' ? 'Importer Package de Sync' : 'Import Sync Package'}
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' 
                    ? 'Importe depuis un fichier (vérifie les doublons)' 
                    : 'Import from file (checks for duplicates)'}
                </p>
                <Button 
                  variant="outline" 
                  className="w-full border-green-600 text-green-700 hover:bg-green-100"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) handleImportSyncPackage(file);
                    };
                    input.click();
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {language === 'fr' ? 'Choisir Fichier' : 'Choose File'}
                </Button>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                💡 {language === 'fr' 
                  ? 'Astuce: Utilisez le Package de Sync pour transférer vos données entre Preview et Production après un déploiement.'
                  : 'Tip: Use the Sync Package to transfer your data between Preview and Production after deployment.'}
              </p>
            </div>
            
            {/* Password Reset Tool */}
            <div className="border-t pt-6 mt-6">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5 text-red-600" />
                {language === 'fr' ? 'Réinitialiser un mot de passe' : 'Reset a password'}
              </h4>
              <div className="p-4 rounded-xl border-2 border-dashed border-red-300 bg-red-50/50">
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' 
                    ? 'Utilisez cet outil pour réinitialiser le mot de passe d\'un utilisateur (convertit en bcrypt).' 
                    : 'Use this tool to reset a user password (converts to bcrypt).'}
                </p>
                <div className="space-y-3">
                  <Input 
                    placeholder={language === 'fr' ? "Email de l'utilisateur" : "User email"}
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    type="email"
                  />
                  <Input 
                    placeholder={language === 'fr' ? "Nouveau mot de passe" : "New password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    type="text"
                  />
                  <Button 
                    variant="destructive"
                    className="w-full"
                    onClick={handleResetPassword}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
